import React, { useEffect, useId, useState } from 'react';
import Select, {
  GroupBase,
  Props as SelectProps,
  StylesConfig,
  components, // Add this
  DropdownIndicatorProps, // Add this
} from 'react-select';

// --- Custom React-Select Styles to match native FloatingInput ---
// Converted to a generic function to satisfy strict TypeScript constraints
const getSelectStyles = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(): StylesConfig<Option, IsMulti, Group> => ({
  control: (base, state) => ({
    ...base,
    height: '50px',
    minHeight: '50px',
    backgroundColor: state.isDisabled ? '#F9FAFB' : 'var(--color-brand-cream)',
    borderRadius: 'var(--radius-artisan)',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    boxShadow: state.isFocused
      ? ' 0 1px 3px rgba(85, 75, 65, 0.12), 0 1px 2px rgba(85, 75, 65, 0.2)'
      : '',
    borderWidth: '1px',
    borderColor: state.isDisabled
      ? '#E5E7EB'
      : state.isFocused
        ? 'var(--color-brand-frost)'
        : 'var(--color-brand-truffle)',
    fontFamily: 'var(--font-display)',
    '&:hover': {
      borderColor: 'var(--color-brand-truffle)',
    },
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 16px',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: '#000000',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#000000',
    margin: 0,
  }),
  placeholder: (base) => ({
    ...base,
    display: 'none',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? '#000000' : '#9CA3AF',
    padding: '0 16px',
    '&:hover': {
      color: '#000000',
    },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '10px',
    marginTop: '8px',
    border: '1px solid #E5E7EB',
    boxShadow:
      '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    zIndex: 50,
    overflow: 'hidden',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#000000'
      : state.isFocused
        ? '#F3F4F6'
        : '#FFFFFF',
    color: state.isSelected ? '#FFFFFF' : '#000000',
    cursor: 'pointer',
    padding: '12px 16px',
    '&:active': {
      backgroundColor: '#E5E7EB',
    },
  }),
});

// Extend react-select props to require our custom label
interface FloatingSelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> extends SelectProps<Option, IsMulti, Group> {
  label: string;
  name?: string;
  hiddenNames?: { label: string; value: string };
  isLocked?: boolean; // Add this
  lockMessage?: string; // Add this
}

export const FloatingSelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  label,
  id,
  value,
  onFocus,
  onBlur,
  name = '',
  hiddenNames,
  onChange,
  defaultValue,
  isLocked = false, // Destructure here
  lockMessage = 'This field is locked', // Destructure here
  components: customComponents, // Destructure existing components
  ...props
}: FloatingSelectProps<Option, IsMulti, Group>) => {
  // Generate reliable IDs for accessibility
  const uniqueId = useId();
  const selectId = id || uniqueId;

  // Track focus state manually since CSS 'peer' cannot traverse react-select's DOM
  const [isFocused, setIsFocused] = useState(false);

  // 1. Track the internal value so it works perfectly even if the parent form
  // doesn't strictly control the `value` prop via state.
  const [internalValue, setInternalValue] = useState<any>(() =>
    value !== undefined ? value : defaultValue,
  );

  // Keep in sync if the parent *does* explicitly update the `value` prop
  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  // 2. Robust checker handling objects, arrays (for isMulti), and strings
  const checkHasValue = (val: any) => {
    if (val === null || val === undefined) return false;
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'string') return val.trim().length > 0;
    if (typeof val === 'object') return Object.keys(val).length > 0;
    return true;
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  const handleChange = (newValue: any, actionMeta: any) => {
    setInternalValue(newValue); // Update our local tracker immediately
    if (onChange) {
      onChange(newValue, actionMeta);
    }
  };

  // 3. The label floats if it is actively focused OR if we know an option is selected
  const isFloating = isFocused || checkHasValue(internalValue);

  return (
    <div className="relative w-full">
      <Select
        inputId={selectId}
        value={value}
        onFocus={handleFocus}
        onBlur={handleBlur}
        styles={getSelectStyles<Option, IsMulti, Group>()}
        onChange={(newValue: any, actionMeta: any) => {
          handleChange(newValue, actionMeta);
        }}
        // 2. Force the disabled state if locked
        isDisabled={isLocked || props.isDisabled}
        defaultValue={defaultValue}
        components={{
          ...customComponents,
          ...(isLocked && {
            DropdownIndicator: (
              indicatorProps: DropdownIndicatorProps<Option, IsMulti, Group>,
            ) => (
              <components.DropdownIndicator {...indicatorProps}>
                <div
                  title={lockMessage}
                  className="cursor-not-allowed text-black/60 hover:text-black transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="3"
                      y="11"
                      width="18"
                      height="11"
                      rx="2"
                      ry="2"
                    ></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                </div>
              </components.DropdownIndicator>
            ),
            ClearIndicator: () => null,
          }),
        }}
        {...props}
      />

      {name && (
        <>
          {/* 4. Added type="hidden" and readOnly to prevent React console warnings */}
          <input
            type="hidden"
            name={hiddenNames?.label ?? `${name}-label`}
            value={(value as any)?.label || (defaultValue as any)?.label || ''}
            readOnly
          />
          <input
            type="hidden"
            name={hiddenNames?.value ?? name}
            value={(value as any)?.value || (defaultValue as any)?.value || ''}
            readOnly
          />
        </>
      )}

      <label
        htmlFor={selectId}
        className={`absolute left-3 px-1 bg-brand-cream transition-all duration-200 pointer-events-none !text-text-muted z-10
          ${
            isFloating
              ? 'top-0 -translate-y-1/2 text-[12px] font-[500] tracking-widest text-black/40' // Floating State
              : 'top-1/2 -translate-y-1/2 text-[16px] font-[400] text-black/60' // Resting State
          }
        `}
      >
        {label}
      </label>
    </div>
  );
};
